/**
 * RUB-49618: context-coverage view.
 *
 * Shows the latest context-coverage snapshot (TechDocs, API specs, declared vs
 * proposed dependencies, MCP usage) next to the recorded baseline. The data
 * comes from the rubicon backend (`/context-coverage/latest` and `/baseline`),
 * which reads the snapshot documents the context-coverage workflow in
 * rubicon-platform-infrastructure writes to S3. Each dimension carries its own
 * source status, so one broken source degrades one card, not the page.
 */

import {
  Content,
  EmptyState,
  Header,
  Link,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Box, Chip, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { RepoTable } from './RepoTable';
import { SummaryCards } from './SummaryCards';
import type { LoadResult, SnapshotResponse } from './types';

export const RUNBOOK_URL =
  'https://github.com/Mercell/rubicon-platform-infrastructure/blob/main/docs/platform/runbooks/context-coverage.md';

/** Developer hub: how a team raises its own row (TechDocs, API specs, dependencies). */
export const IMPROVE_ROW_URL =
  'https://github.com/Mercell/rubicon-platform-infrastructure/blob/main/docs/developers/tasks/your-service-in-the-catalog.md#read-your-context-coverage-row';

type FetchFn = typeof fetch;

const EMPTY_DIMENSIONS: NonNullable<
  SnapshotResponse['snapshot']['dimensions']
> = {};
const EMPTY_REPOS: NonNullable<SnapshotResponse['snapshot']['repos']> = [];

function assertNever(value: never): never {
  throw new Error(`unhandled load state: ${JSON.stringify(value)}`);
}

/** Readable label for the <time> element; the ISO value stays in dateTime. */
export function formatGeneratedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  })} UTC`;
}

/** Only https GitHub run URLs are linked; the backend filters too (defence in depth). */
export function linkableRunUrl(
  value: string | null | undefined,
): string | null {
  return typeof value === 'string' && /^https:\/\/github\.com\//.test(value)
    ? value
    : null;
}

/**
 * Shape check on the backend envelope before trusting it. It verifies exactly
 * what the `SnapshotResponse` type promises: `snapshot.generated_at`,
 * `age_hours` and `stale`; every other snapshot field is optional in the type
 * and read defensively.
 */
export function isSnapshotResponse(value: unknown): value is SnapshotResponse {
  if (typeof value !== 'object' || value === null) return false;
  const envelope = value as {
    snapshot?: unknown;
    age_hours?: unknown;
    stale?: unknown;
  };
  const snapshot = envelope.snapshot as { generated_at?: unknown } | null;
  return (
    typeof snapshot === 'object' &&
    snapshot !== null &&
    typeof snapshot.generated_at === 'string' &&
    typeof envelope.age_hours === 'number' &&
    typeof envelope.stale === 'boolean'
  );
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      error?: unknown;
      detail?: unknown;
    };
    if (typeof body.error === 'string' && body.error) {
      return typeof body.detail === 'string' && body.detail
        ? `${body.error} (${body.detail})`
        : body.error;
    }
  } catch {
    // Not a JSON error body - fall through to the status line.
  }
  return `HTTP ${response.status}${
    response.statusText ? ` ${response.statusText}` : ''
  }`;
}

export async function loadSnapshot(
  fetchFn: FetchFn,
  baseUrl: string,
  name: 'latest' | 'baseline',
  signal?: AbortSignal,
): Promise<LoadResult> {
  try {
    const response = await fetchFn(
      `${baseUrl}/context-coverage/${name}`,
      signal ? { signal } : undefined,
    );
    if (response.ok) {
      const data: unknown = await response.json();
      if (!isSnapshotResponse(data)) {
        return {
          kind: 'error',
          message: 'unexpected response shape from the backend',
        };
      }
      return { kind: 'ok', data };
    }
    if (response.status === 404) return { kind: 'not-found' };
    const message = await errorMessage(response);
    if (response.status === 501) return { kind: 'not-configured', message };
    return { kind: 'error', message };
  } catch (error) {
    return {
      kind: 'error',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

function Provenance({ data }: { data: SnapshotResponse }) {
  const { snapshot, age_hours: ageHours, stale } = data;
  const runUrl = linkableRunUrl(snapshot.run_url);
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" mb={3} gridGap={8}>
      <Typography variant="body2" color="textSecondary">
        Snapshot generated{' '}
        <time dateTime={snapshot.generated_at}>
          {formatGeneratedAt(snapshot.generated_at)}
        </time>{' '}
        ({ageHours} h ago) by{' '}
        {runUrl ? (
          <Link to={runUrl}>run {snapshot.run_id ?? 'unknown'}</Link>
        ) : (
          `run ${snapshot.run_id ?? 'unknown'}`
        )}
        {snapshot.window_days
          ? `, usage window ${snapshot.window_days} days`
          : ''}
        .
      </Typography>
      {stale && (
        <>
          <Chip label="stale" size="small" color="secondary" />
          {/* Visible text, not a tooltip: the chip is not focusable. */}
          <Typography variant="body2" color="textSecondary">
            The snapshot is older than 48 hours; the daily run may be failing.
          </Typography>
        </>
      )}
    </Box>
  );
}

export function ContextCoveragePage() {
  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [latest, setLatest] = useState<LoadResult>({ kind: 'loading' });
  const [baseline, setBaseline] = useState<LoadResult>({ kind: 'loading' });

  useEffect(() => {
    // Abort the in-flight requests on unmount; `cancelled` still guards the
    // discovery step and the state writes after an abort resolves.
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      let baseUrl: string;
      try {
        baseUrl = await discoveryApi.getBaseUrl('rubicon');
      } catch (error) {
        if (!cancelled) {
          const failed: LoadResult = { kind: 'error', message: String(error) };
          setLatest(failed);
          setBaseline(failed);
        }
        return;
      }
      const [latestResult, baselineResult] = await Promise.all([
        loadSnapshot(fetch, baseUrl, 'latest', controller.signal),
        loadSnapshot(fetch, baseUrl, 'baseline', controller.signal),
      ]);
      if (!cancelled) {
        setLatest(latestResult);
        setBaseline(baselineResult);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [discoveryApi, fetch]);

  // The status live region is always mounted and only its children change,
  // so screen readers announce loading, "nothing to show" and completion;
  // errors use a separate alert; the loaded content is never announced
  // wholesale.
  let status: JSX.Element | null;
  switch (latest.kind) {
    case 'loading':
      status = (
        <>
          <Typography variant="srOnly">Loading context coverage…</Typography>
          <Progress />
        </>
      );
      break;
    case 'not-configured':
      status = (
        <EmptyState
          missing="info"
          title="Context coverage is not configured"
          description="The backend has no snapshot bucket (rubicon.contextCoverage.bucket, env CONTEXT_COVERAGE_BUCKET), so there is nothing to show in this environment."
        />
      );
      break;
    case 'not-found':
      status = (
        <EmptyState
          missing="data"
          title="No snapshot yet"
          description={
            <>
              The context-coverage workflow has not written a snapshot yet.
              Dispatch it as described in the{' '}
              <Link to={RUNBOOK_URL}>context-coverage runbook</Link>.
            </>
          }
        />
      );
      break;
    case 'error':
    case 'ok':
      status = null;
      break;
    default:
      status = assertNever(latest);
  }

  return (
    <Page themeId="tool">
      <Header
        title="Context coverage"
        subtitle="How much of the fleet's context (docs, API specs, dependencies) is available to people and agents"
      />
      <Content>
        <div role="status" aria-live="polite">
          {status}
        </div>
        {latest.kind === 'error' && (
          <div role="alert">
            <WarningPanel
              severity="error"
              title="Could not load context coverage"
              message={latest.message}
              defaultExpanded
            />
          </div>
        )}
        {latest.kind === 'ok' && (
          <>
            <Typography variant="body2" paragraph>
              <Link to={IMPROVE_ROW_URL}>How to improve your row</Link>: the
              developer guides for TechDocs, API specs and service dependencies.
            </Typography>
            <Provenance data={latest.data} />
            <Typography variant="h5" component="h2" gutterBottom>
              Fleet summary
            </Typography>
            <SummaryCards
              latest={latest.data.snapshot.dimensions ?? EMPTY_DIMENSIONS}
              baseline={baseline}
            />
            <Box mt={4}>
              <Typography variant="h5" component="h2" gutterBottom>
                Per-repo coverage
              </Typography>
              <RepoTable repos={latest.data.snapshot.repos ?? EMPTY_REPOS} />
            </Box>
          </>
        )}
      </Content>
    </Page>
  );
}
